/**
 * Map poster editor.
 *
 * Renders a print-style map poster from **pre-built vector tiles** (Protomaps
 * PMTiles) via MapLibre GL, and lets the customer frame it by dragging and
 * zooming.
 *
 *   PMTiles archive (R2) -> MapLibre GL -> styled layers -> poster
 *
 * Why tiles rather than a live query: an earlier version asked the Overpass API
 * for raw OSM geometry on every load. That was 7-16 seconds, 17 MB, and depended
 * entirely on a free public endpoint that rate-limits and eventually refuses
 * connections. Tiles are static files on a CDN: instant, uncapped, and no
 * third party can turn the feature off.
 *
 * The map layers are recoloured from the palette, which is why the palettes
 * work at all — you cannot restyle a raster tile.
 *
 * OSM data is ODbL: the attribution is a licence requirement, not decoration.
 */

// Archive URL comes from the section setting so a different region can be
// pointed at without editing code.
const DEFAULT_TILE_URL = 'https://framelane-uploads.framelane.workers.dev/f/tiles/world-z11.pmtiles';
const NOMINATIM = 'https://nominatim.openstreetmap.org/search';

// Photon is the primary search. It is built for type-ahead, so partial and
// slightly-off input still returns a page of candidates — "san fran" and "nyc"
// both work — where Nominatim's exact matching often returns one or two rows
// ("mumbai" returns a single result). Nominatim stays as the fallback so the
// box still works if Photon is unavailable.
const PHOTON = 'https://photon.komoot.io/api/';

// Panning is clamped to the extent of the tile archive, so the customer cannot
// drag into blank space where no tiles exist. The default matches the shipped
// world archive; `tileBounds()` ignores a whole-world box on purpose.
const DEFAULT_BOUNDS = [[-180, -85], [180, 85]];

const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
const PMTILES_JS = 'https://unpkg.com/pmtiles@3.2.1/dist/pmtiles.js';

const FONT_STACK = "'Avenir Next', Poppins, Montserrat, Helvetica, Arial, sans-serif";

// Protomaps v4 road kinds, widest first. The spread is deliberately wide: the
// reference posters read as a few heavy arteries over a fine grid, so majors
// are several times the weight of minors rather than a gentle ramp.
const ROAD_KINDS = [
  { kinds: ['highway'], key: 'motorway', width: 2.2 },
  { kinds: ['major_road'], key: 'primary', width: 1.5 },
  { kinds: ['medium_road'], key: 'secondary', width: 0.95 },
  { kinds: ['minor_road'], key: 'minor', width: 0.45 },
  { kinds: ['path'], key: 'path', width: 0.3 },
];

// Every fill layer must declare that it only wants polygon geometry. A fill
// handed a LineString triangulates it as if it were a ring, painting a fan of
// straight-edged wedges across the map. The `water` source-layer carries river
// centrelines as well as water polygons, so without this the rivers smear into
// large angular blobs — Pune was the worst case. The official Protomaps styles
// guard the same way with ["==", "$type", "Polygon"]; both Polygon and
// MultiPolygon are listed here so this holds whichever way geometry-type
// normalises multi-geometries.
const POLYGONS_ONLY = ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]];

// How much of a searched place's bounding box to frame. A city's box is its
// administrative extent, which is mostly outskirts — fit all of it and the
// built-up centre ends up a small patch in the middle of the poster. Pulling
// the box in towards its centre puts the street grid in the frame instead,
// which is what makes the map read as dense. 1 would fit the box exactly.
const FRAME_TIGHTEN = 0.6;

const LABEL_STYLES = [
  'below-left',
  'below-center',
  'below-right',
  'above-left',
  'above-center',
  'above-right',
];

// Human labels, also used for the thumbnail aria-labels.
const LABEL_STYLE_NAMES = {
  'below-left': 'Below left',
  'below-center': 'Below centre',
  'below-right': 'Below right',
  'above-left': 'Above left',
  'above-center': 'Above centre',
  'above-right': 'Above right',
};

// Caption typefaces. Cormorant is the storefront's own heading font, so it
// costs nothing to load; the other two come from Google Fonts, requested in the
// section markup at only the two weights the caption uses.
//
// `scale` corrects for optical size. At the same font-size the three faces do
// not read the same size — measured cap heights for "NEW DELHI" at 100px are
// Cormorant 62.7, Playfair 70.8, Jost 73.5. Without this, switching font
// silently changes how large the caption looks. Playfair is the reference.
const FONTS = [
  { id: 'cormorant', label: 'Cormorant', stack: 'var(--font-heading-family)', scale: 1.13 },
  {
    id: 'playfair',
    label: 'Playfair',
    stack: "'Playfair Display', Georgia, 'Times New Roman', serif",
    scale: 1,
  },
  {
    id: 'jost',
    label: 'Jost',
    stack: "'Jost', 'Century Gothic', 'Helvetica Neue', Arial, sans-serif",
    scale: 0.96,
  },
];

// The moulding. Colours come from photo-frame-sizes.json, the same source the
// photo-frame customizer reads, so the two cannot drift apart. The product's own
// Colour option selects one; the sheen is a single diagonal gradient that reads
// as a bevelled edge.
const FRAME_SHEENS = {
  dark: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0.28) 100%)',
  light: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(0,0,0,0.04) 55%, rgba(0,0,0,0.10) 100%)',
  none: 'none',
};

// Moulding width as a share of the poster width. Deliberately slim: the frame
// reads as a hairline around the print, not a chunky border. "Only Print" gets
// none at all.
const FRAME_PAD = '0.8%';

// The red pin dropped on the searched location. Drawn inline so it needs no
// sprite sheet and no extra request.
const PIN_SVG = `<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">
  <path d="M12 0C5.37 0 0 5.37 0 12c0 8.5 12 20 12 20s12-11.5 12-20C24 5.37 18.63 0 12 0z" fill="#c0392b"/>
  <circle cx="12" cy="12" r="4.3" fill="#ffffff"/>
</svg>`;

/**
 * Hex colour to rgba(). The palettes carry opaque colours only, but a building
 * outline at full strength reads as a hard black box; it wants to be a fraction
 * of the ink.
 */
function withAlpha(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Data-driven fill for a confetti palette.
 *
 * A palette may carry a `confetti` array. When it does, the land use and the
 * building fills take their colour from that list, chosen by a hash of the
 * feature id, rather than the palette's single park/building tone. The result
 * is coloured patches across the map; roads and water stay the palette's own
 * white, so the street grid reads as the white space between them.
 *
 * Feature ids are stable, so a given district keeps its colour across reloads
 * and zoom levels. `coalesce` guards the rare feature without an id.
 */
function confettiFill(pal) {
  const colors = pal.confetti;
  if (!Array.isArray(colors) || !colors.length) return null;
  const match = ['match', ['%', ['coalesce', ['id'], 0], colors.length]];
  colors.forEach((c, i) => match.push(i, c));
  match.push(colors[colors.length - 1]);
  return match;
}

/**
 * A tileable confetti swatch for the background-pattern.
 *
 * The land use polygons do not cover the whole map, so the ground showing
 * between them would otherwise read as white and compete with the roads. This
 * paints it as a fine patchwork of the same palette colours instead. Drawn on
 * a canvas rather than shipped as a file, and seeded, so it is identical on
 * every load. The grid divides the tile exactly, so it repeats without seams.
 */
function confettiImage(colors, size = 256, cell = 16) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  let seed = 0x9e3779b9;
  const rnd = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return (seed >>> 0) / 4294967296;
  };
  const n = Math.round(size / cell);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      ctx.fillStyle = colors[Math.floor(rnd() * colors.length)];
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  return ctx.getImageData(0, 0, size, size);
}

/** Load a stylesheet once. */
function loadCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const el = document.createElement('link');
  el.rel = 'stylesheet';
  el.href = href;
  document.head.appendChild(el);
}

/** Load an external script once. */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const el = document.createElement('script');
    el.src = src;
    el.onload = resolve;
    el.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(el);
  });
}

class MapPosterEditor extends HTMLElement {
  async connectedCallback() {
    this.mapEl = this.querySelector('[data-map]');
    this.status = this.querySelector('[data-status]');
    this.statusText = this.querySelector('[data-status-text]');
    this.notice = this.querySelector('[data-notice]');
    this.variants = JSON.parse(this.dataset.variants || '[]');
    window.__mapEditor = this; // debug hook
    this.labelStyle = this.dataset.labelStyle || 'below-left';
    this.font = this.dataset.font || FONTS[0].id;
    this.showBuildings = this.dataset.showBuildings === 'true';
    this.orientation = 'portrait';
    this.formatIndex = 0;
    this.view = {
      lat: parseFloat(this.dataset.startLat) || 28.6139,
      lon: parseFloat(this.dataset.startLon) || 77.209,
      spanM: (parseFloat(this.dataset.widthKm) || 6) * 1000,
    };
    this.place = this.dataset.startPlace || '';
    this.country = '';
    this.overrides = {};
    // The pin starts on the starting place, so the poster shows one from the off
    // rather than only after the customer searches for something.
    this.pin = { lat: this.view.lat, lon: this.view.lon };

    await this.loadPalettes();
    await this.loadFrameColors();
    this.buildPaletteGrid();
    this.buildLabelStyleGrid();
    this.buildFontGrid();
    this.buildFormatGrid();
    this.buildFormFields();
    this.bind();

    try {
      this.setStatus('Loading map…');
      loadCss(MAPLIBRE_CSS);
      await loadScript(MAPLIBRE_JS);
      await loadScript(PMTILES_JS);
      this.initMap();
    } catch (err) {
      this.setStatus('Could not load the map library.', true);
      console.error('[map-poster]', err);
    }
  }

  /* ---------------------------------------------------------------- data */

  async loadPalettes() {
    try {
      this.palettes = await fetch(this.dataset.palettes).then((r) => r.json());
    } catch (err) {
      this.palettes = [];
    }
    this.palette = this.palettes.find((p) => p.id === this.dataset.startPalette) || this.palettes[0];
  }

  /** Frame colours, shared with the photo-frame customizer. */
  async loadFrameColors() {
    if (!this.dataset.frameColors) return;
    try {
      this.frameColors = (await fetch(this.dataset.frameColors).then((r) => r.json())).colors || {};
    } catch (err) {
      this.frameColors = {};
    }
  }

  /* --------------------------------------------------------------- style */

  /**
   * Build a MapLibre style from a palette.
   *
   * Order is ground-up: water, ground cover, zoning, buildings, then roads.
   * Buildings sit under the roads so the street grid stays legible on top.
   */
  buildStyle(pal) {
    // null for the ordinary palettes, an expression for confetti
    const conf = confettiFill(pal);

    const layers = [
      { id: 'background', type: 'background', paint: { 'background-color': pal.bg } },
      {
        id: 'water',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'water',
        filter: POLYGONS_ONLY,
        paint: { 'fill-color': pal.water },
      },
      {
        // Woods, grass, scrub. This layer used to name `natural`, which is not
        // a source-layer in the archive, so it had never rendered anything. The
        // same ground cover lives in `landcover`.
        id: 'landcover',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'landcover',
        filter: POLYGONS_ONLY,
        paint: { 'fill-color': pal.park, 'fill-opacity': 0.7 },
      },
      {
        id: 'landuse',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'landuse',
        filter: POLYGONS_ONLY,
        paint: { 'fill-color': conf || pal.park, 'fill-opacity': conf ? 1 : 0.85 },
      },
    ];

    // Buildings are what makes a city read as dense, but they are heavy, so
    // they stay behind the section's Show buildings setting. Most palettes put
    // `building` very close to the background colour, so the outline is what
    // actually makes the footprints legible.
    if (this.showBuildings) {
      layers.push({
        id: 'buildings',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'buildings',
        filter: POLYGONS_ONLY,
        paint: {
          'fill-color': conf || pal.building,
          'fill-opacity': conf ? 1 : 0.9,
          // full-strength ink reads as a hard black box; a third of it just
          // defines the footprint
          'fill-outline-color': withAlpha(pal.ink, 0.35),
        },
      });
    }

    // roads, thin classes on top, with a casing underneath for definition
    ROAD_KINDS.forEach((r) => {
      layers.push({
        id: `road-casing-${r.key}`,
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['in', ['get', 'kind'], ['literal', r.kinds]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': pal.ink,
          'line-opacity': 0.12,
          'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 6, 0.4 * pal.road_scale, 16, r.width * 6 * pal.road_scale],
        },
      });
    });
    ROAD_KINDS.forEach((r) => {
      layers.push({
        id: `road-${r.key}`,
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        filter: ['in', ['get', 'kind'], ['literal', r.kinds]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': pal.roads[r.key],
          'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 6, 0.25 * pal.road_scale, 16, r.width * 4 * pal.road_scale],
        },
      });
    });

    return {
      version: 8,
      sources: {
        protomaps: {
          type: 'vector',
          url: `pmtiles://${this.tileUrl()}`,
          attribution: '© OpenStreetMap contributors',
        },
      },
      layers,
    };
  }

  /**
   * Give a confetti palette a confetti ground. The background layer takes the
   * generated swatch as a pattern; a palette without a confetti list keeps its
   * plain colour, since the rebuilt style no longer names a pattern.
   */
  applyConfettiPattern() {
    const colors = this.palette && this.palette.confetti;
    if (!this.map || !this.map.getLayer('background')) return;
    if (!Array.isArray(colors) || !colors.length) return;
    const id = 'confetti-ground';
    if (this.map.hasImage(id)) this.map.removeImage(id);
    this.map.addImage(id, confettiImage(colors), { pixelRatio: 1 });
    this.map.setPaintProperty('background', 'background-pattern', id);
  }

  initMap() {
    const protocol = new pmtiles.Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);

    this.map = new maplibregl.Map({
      container: this.mapEl,
      style: this.buildStyle(this.palette),
      maxBounds: this.tileBounds(),
      center: [this.view.lon, this.view.lat],
      zoom: this.spanToZoom(this.view.spanM),
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
      preserveDrawingBuffer: true,
    });

    this.setPin(this.pin.lat, this.pin.lon);

    // The poster is sized from the viewport, so the map has to be told when its
    // container changes size — MapLibre does not observe that itself.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.map && this.map.resize());
      this.resizeObserver.observe(this.mapEl);
    }

    // MapLibre's default wheel zoom is brisk enough that one flick jumps several
    // levels. Halving both rates makes framing a place controllable.
    this.map.scrollZoom.setWheelZoomRate(1 / 900);
    this.map.scrollZoom.setZoomRate(1 / 200);

    // Re-applied on every style load, which covers the initial paint and each
    // palette switch, because setStyle rebuilds the layers and drops images.
    this.map.on('style.load', () => this.applyConfettiPattern());

    this.map.on('load', () => {
      this.hideStatus();
      this.map.resize();
      this.syncFromMap();
      // moveend does not fire on first load, so fill the country line here
      this.reverseGeocodeOnce();
    });
    this.map.on('error', (e) => {
      console.warn('[map-poster] map error', e && e.error);
    });
    this.map.on('move', () => this.syncFromMap());
    // syncFromMap bails while the map is animating, so re-sync once it settles
    // — otherwise the cart properties keep the pre-animation span.
    this.map.on('moveend', () => {
      this.syncFromMap();
      this.reverseGeocodeOnce();
    });

    this.querySelector('[data-zoom-in]')?.addEventListener('click', () => this.map.zoomIn());
    this.querySelector('[data-zoom-out]')?.addEventListener('click', () => this.map.zoomOut());
  }

  /** The tile archive to read from. */
  tileUrl() {
    return (this.dataset.tileUrl || '').trim() || DEFAULT_TILE_URL;
  }

  /** The archive's geographic extent, from a setting or the built-in default.
   *
   * Returns undefined for a world-spanning archive, and that is deliberate:
   * passing MapLibre a maxBounds covering the whole planet makes its constrain
   * logic clamp the zoom to maxZoom, so the map opens at z22 showing nothing.
   * World archives need no clamping anyway — there is nowhere to pan off to. */
  tileBounds() {
    let box = DEFAULT_BOUNDS;
    const raw = this.dataset.bounds;
    if (raw) {
      const parts = raw.split(',').map(Number);
      if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
        box = [[parts[0], parts[1]], [parts[2], parts[3]]];
      }
    }
    const [[w, s], [e, n]] = box;
    const wholeWorld = w <= -179 && e >= 179 && s <= -84 && n >= 84;
    return wholeWorld ? undefined : box;
  }

  /* ------------------------------------------------------------ view <-> map */

  /** Metres per pixel at zoom 0, on the 512px world tile scheme. */
  static mpp0() {
    return 40075016.686 / 512;
  }

  /** The zoom at which `spanM` metres fits across the visible map box. */
  spanToZoom(spanM, lat) {
    const px = this.mapEl?.clientWidth || 840;
    const latitude = lat === undefined ? this.view.lat : lat;
    return Math.log2(
      (MapPosterEditor.mpp0() * Math.cos((latitude * Math.PI) / 180) * px) / spanM
    );
  }

  /** Ground metres across the visible map box at a given zoom. */
  zoomToSpan(zoom, lat) {
    const px = this.mapEl?.clientWidth || 840;
    const latitude = lat === undefined ? this.map.getCenter().lat : lat;
    return (MapPosterEditor.mpp0() * Math.cos((latitude * Math.PI) / 180) * px) / Math.pow(2, zoom);
  }

  syncFromMap() {
    // getCenter() returns {0,0} until the style has loaded — reading it early
    // writes a bogus 0°00'00" into the caption and the cart properties
    if (!this.map || !this.map.loaded()) return;
    const c = this.map.getCenter();
    this.view.lat = c.lat;
    this.view.lon = c.lng;
    this.view.spanM = this.zoomToSpan(this.map.getZoom(), this.view.lat);
    this.updateCaption();
    this.updateForm();
  }

  /* ---------------------------------------------------------------- map ui */

  /**
   * Nominatim lookup for one of the two search boxes. `kind` selects which
   * suggestion list to fill and what applyPlace writes back into the input.
   */
  async suggest(query, kind = 'address') {
    const box = this.suggestBox(kind);
    if (!box) return;
    if (!query || query.length < 3) return this.hideSuggest(kind);

    let results = [];
    try {
      results = await this.searchPhoton(query);
    } catch (err) {
      results = [];
    }
    if (!results.length) {
      try {
        results = await this.searchNominatim(query);
      } catch (err) {
        results = [];
      }
    }
    if (!results.length) return this.hideSuggest(kind);

    box.innerHTML = '';
    results.forEach((r) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-editor__suggest-item';
      btn.textContent = r.display_name;
      btn.addEventListener('click', () => {
        this.applyPlace(r, kind);
        this.hideSuggest(kind);
      });
      box.appendChild(btn);
    });
    box.hidden = false;
  }

  /**
   * Photon returns GeoJSON, with the useful parts spread across `properties`.
   * Reshape into what applyPlace expects. Its `extent` is
   * [minLon, minLat, maxLon, maxLat]; Nominatim's boundingbox is
   * [south, north, west, east], so the order is swapped on the way through.
   *
   * Deliberately NOT biased to the current view. Photon accepts lat/lon for
   * that and weights it hard enough to dominate: with the map parked on New
   * Delhi, "eiffel tower" returns an Eiffel Tower *restaurant in Delhi* ahead
   * of the Paris landmark, and "bombay" returns Delhi shops. A map poster is
   * usually of somewhere you are not, so proximity is the wrong prior here.
   */
  async searchPhoton(query) {
    const url = `${PHOTON}?q=${encodeURIComponent(query)}&limit=8&lang=en`;
    const data = await fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const seen = new Set();
    return (data.features || [])
      .map((f) => {
        const p = f.properties || {};
        const [lon, lat] = f.geometry.coordinates;
        const ext = p.extent;
        return {
          lat,
          lon,
          display_name: [p.name, p.city, p.state, p.country].filter(Boolean).join(', '),
          boundingbox: ext
            ? [String(ext[1]), String(ext[3]), String(ext[0]), String(ext[2])]
            : [],
          // cityFrom reads address.city, but Photon's `city` is the containing
          // municipality — for Jaipur that is "Jaipur Municipal Corporation",
          // which is not what belongs under the map. When the hit is itself a
          // place (osm_key "place") its own name is the right label; otherwise
          // the containing city is, so a landmark is captioned with its city.
          address: {
            city: p.osm_key === 'place' ? p.name : p.city || p.name,
            state: p.state,
            country: p.country,
          },
        };
      })
      .filter((r) => {
        if (!r.display_name || seen.has(r.display_name)) return false;
        seen.add(r.display_name);
        return true;
      });
  }

  async searchNominatim(query) {
    const url = `${NOMINATIM}?q=${encodeURIComponent(query)}&format=json&limit=8&addressdetails=1&dedupe=1`;
    return fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  suggestBox(kind) {
    return this.querySelector(kind === 'city' ? '[data-city-suggest]' : '[data-suggest]');
  }

  hideSuggest(kind = 'address') {
    const box = this.suggestBox(kind);
    if (box) box.hidden = true;
  }

  /**
   * Drop the pin. It is anchored to the location rather than the frame centre,
   * so it stays on the place you pinned when you drag to frame around it.
   */
  setPin(lat, lon) {
    this.pin = { lat, lon };
    if (!this.map) return;
    if (!this.pinMarker) {
      const el = document.createElement('div');
      el.className = 'map-editor__pin';
      el.innerHTML = PIN_SVG;
      this.pinMarker = new maplibregl.Marker({ element: el, anchor: 'bottom' });
    }
    this.pinMarker.setLngLat([lon, lat]).addTo(this.map);
  }

  applyPlace(hit, kind = 'address') {
    const lat = parseFloat(hit.lat);
    const lon = parseFloat(hit.lon);
    this.place = this.cityFrom(hit);
    this.country = (hit.address || {}).country || '';
    // The city box keeps the short name; "put a pin on it" keeps the full
    // address, which is the thing the customer typed their way towards.
    const input = this.querySelector(kind === 'city' ? '[data-city]' : '[data-address]');
    if (input) input.value = kind === 'city' ? this.cityFrom(hit) : hit.display_name;
    this.view.lat = lat;
    this.view.lon = lon;
    this.setPin(lat, lon);

    if (this.map) {
      // Fit to the place's own extent rather than a fixed span, so a search
      // frames the whole city outline the way the reference posters do. A
      // fixed span is either too tight for a large city or too wide for a
      // small one. Nominatim returns boundingbox as [south, north, west, east].
      const bb = (hit.boundingbox || []).map(Number);
      if (bb.length === 4 && bb.every((n) => Number.isFinite(n))) {
        const [south, north, west, east] = bb;
        const cLat = (south + north) / 2;
        const cLon = (west + east) / 2;
        const halfLat = ((north - south) / 2) * FRAME_TIGHTEN;
        const halfLon = ((east - west) / 2) * FRAME_TIGHTEN;
        this.map.fitBounds(
          [
            [cLon - halfLon, cLat - halfLat],
            [cLon + halfLon, cLat + halfLat],
          ],
          { padding: 12, maxZoom: 15, duration: 900 }
        );
      } else {
        this.map.flyTo({ center: [lon, lat], zoom: this.spanToZoom(this.view.spanM, lat), duration: 900 });
      }
    }
    this.updateCaption();
    this.updateForm();
  }

  cityFrom(hit) {
    const a = hit.address || {};
    return a.city || a.town || a.village || a.municipality || a.county || (hit.display_name || '').split(',')[0];
  }

  /** Fill the country line from the coordinates, once. */
  async reverseGeocodeOnce() {
    if (this.reverseDone || this.country) return;
    this.reverseDone = true;
    try {
      const url = `${NOMINATIM.replace('/search', '/reverse')}?lat=${this.view.lat}&lon=${this.view.lon}&format=json&addressdetails=1`;
      const hit = await fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (hit && hit.address) {
        if (!this.dataset.startPlace) this.place = this.cityFrom(hit);
        this.country = hit.address.country || '';
        this.updateCaption();
        this.updateForm();
      }
    } catch (err) {
      /* the caption just stays as-is */
    }
  }

  /* -------------------------------------------------------------- caption */

  updateCaption() {
    const o = this.overrides || {};
    const title = (o.place || this.place || '').toUpperCase();
    const sub = (o.country || this.country || '').toUpperCase();
    const coords = o.coords || `${dms(this.view.lat, true)} / ${dms(this.view.lon, false)}`;
    const set = (sel, value) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = value;
    };
    set('[data-cap-title]', title);
    set('[data-cap-sub]', sub);
    set('[data-cap-coords]', coords);
  }

  /* ---------------------------------------------------------------- frame */

  /** The product's own Colour option picks the moulding. */
  selectedColour() {
    const checked = document.querySelector('input[type="radio"][name^="Colour"]:checked');
    return (checked && checked.value) || 'Black';
  }

  /**
   * Paint the moulding. "Only Print" has no frame, so the padding collapses and
   * the poster's white runs to the edge — which is how it looked before framing
   * existed at all.
   */
  applyFrameColor() {
    if (!this.frame) return;
    const def = (this.frameColors || {})[this.selectedColour()] || {};
    const printOnly = !def.frame;
    this.frame.style.setProperty('--frame-color', def.frame || '#ffffff');
    this.frame.style.setProperty('--frame-pad', printOnly ? '0%' : FRAME_PAD);
    this.frame.style.setProperty('--frame-sheen', FRAME_SHEENS[def.sheen] || 'none');
  }

  /* ------------------------------------------------------------------ ui */

  buildPaletteGrid() {
    const grid = this.querySelector('[data-palettes-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    this.palettes.forEach((p) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-editor__palette';
      btn.dataset.palette = p.id;
      btn.innerHTML = `
        <span class="map-editor__palette-chip" style="--c1:${p.swatch[0]};--c2:${p.swatch[2]}"></span>
        <span class="map-editor__palette-name">${p.label}</span>`;
      btn.addEventListener('click', () => {
        this.palette = p;
        this.syncPaletteGrid();
        if (this.map) this.map.setStyle(this.buildStyle(p));
        this.updateForm();
      });
      grid.appendChild(btn);
    });
    this.syncPaletteGrid();
  }

  syncPaletteGrid() {
    this.querySelectorAll('[data-palette]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.palette === this.palette?.id);
    });
  }

  buildLabelStyleGrid() {
    const grid = this.querySelector('[data-label-styles]');
    if (!grid) return;
    grid.innerHTML = '';
    LABEL_STYLES.forEach((id) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-editor__label-style';
      btn.dataset.labelStyle = id;
      btn.setAttribute('aria-label', LABEL_STYLE_NAMES[id] || id);
      btn.innerHTML = `
        <span class="map-editor__label-thumb map-editor__label-thumb--${id}"></span>
        <span class="map-editor__label-style-name">${LABEL_STYLE_NAMES[id] || id}</span>`;
      btn.addEventListener('click', () => {
        this.labelStyle = id;
        this.frame.dataset.label = id;
        this.syncLabelStyleGrid();
        this.updateForm();
      });
      grid.appendChild(btn);
    });
    this.syncLabelStyleGrid();
  }

  syncLabelStyleGrid() {
    this.querySelectorAll('button[data-label-style]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.labelStyle === this.labelStyle);
    });
  }

  buildFontGrid() {
    const grid = this.querySelector('[data-fonts-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    FONTS.forEach((f) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-editor__font';
      btn.dataset.font = f.id;
      // render each name in its own face, so the button previews the choice
      btn.style.fontFamily = f.stack;
      btn.textContent = f.label;
      btn.addEventListener('click', () => this.setFont(f.id));
      grid.appendChild(btn);
    });
    this.syncFontGrid();
  }

  syncFontGrid() {
    this.querySelectorAll('button[data-font]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.font === this.font);
    });
  }

  setFont(id) {
    this.font = id;
    this.syncFontGrid();
    this.applyFont();
    this.updateForm();
  }

  applyFont() {
    if (!this.frame) return;
    const f = FONTS.find((x) => x.id === this.font) || FONTS[0];
    this.frame.style.setProperty('--cap-font', f.stack);
    this.frame.style.setProperty('--cap-scale', f.scale);
  }

  buildFormatGrid() {
    const grid = this.querySelector('[data-formats-grid]');
    if (!grid) return;
    if (!this.variants.length) {
      grid.innerHTML = '<p class="map-editor__notice">No product assigned.</p>';
      return;
    }
    grid.innerHTML = '';
    this.variants.forEach((v, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-editor__format';
      btn.dataset.formatIndex = String(i);
      btn.innerHTML = `<span>${v.title}</span><span class="map-editor__format-price">${this.money(v.price)}</span>`;
      btn.addEventListener('click', () => {
        this.formatIndex = i;
        this.syncFormatGrid();
        this.updateForm();
      });
      grid.appendChild(btn);
    });
    this.syncFormatGrid();
  }

  syncFormatGrid() {
    this.querySelectorAll('[data-format-index]').forEach((el) => {
      el.classList.toggle('is-active', Number(el.dataset.formatIndex) === this.formatIndex);
    });
  }

  buildFormFields() {
    // On a product page the theme already renders a product form with the
    // variant picker and add-to-cart. Two carts would be wrong, so stand our
    // own form down and feed the recipe into the theme's form instead.
    this.ownForm = this.querySelector('[data-own-form]');
    // A product page can carry several /cart/add forms; the one that matters is
    // the one with the add-to-cart button, not simply the first in the DOM.
    const cartForms = [...document.querySelectorAll('form[action*="/cart/add"]:not([data-own-form])')];
    this.themeForm = cartForms.find((f) => f.querySelector('button[name="add"]')) || cartForms[0] || null;
    if (this.themeForm) {
      // The theme's variant pickers (colour / material / size) ARE the format
      // selector on a product page. Our own list would repeat all 84 variants
      // as a flat list, so it stands down.
      this.querySelector('[data-format-field]')?.setAttribute('hidden', '');
      this.formatIndex = 0;
    }
    if (this.themeForm && this.ownForm) {
      this.ownForm.closest('.map-editor__buy')?.setAttribute('hidden', '');
    }

    // On a product page the theme's form is the one that actually submits, so
    // the recipe inputs must live there; only fall back to our own form when
    // there is no theme form (a standalone editor page).
    const form = this.themeForm || this.ownForm;
    if (!form) return;
    form.addEventListener('submit', (event) => this.onSubmit(event), true);
    this.formFields = {};
    ['Latitude', 'Longitude', 'Pin', 'Span (m)', 'Palette', 'Place', 'Country', 'Caption', 'Orientation', 'Format', 'Label style', 'Font', 'Zoom', '_Poster preview', '_Poster thumbnail']
      .forEach((name) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        form.appendChild(input);
        this.formFields[name] = input;
      });
  }

  bind() {
    this.frame = this.querySelector('[data-frame]');
    if (this.frame) this.frame.dataset.label = this.labelStyle;
    this.applyFrameColor();
    this.applyFont();

    // The theme re-renders the variant picker whenever a variant changes, so
    // delegate from the document rather than binding to the radios themselves.
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t instanceof HTMLInputElement && t.type === 'radio' && /^Colour/.test(t.name)) {
        this.applyFrameColor();
      }
    });

    // Both search boxes behave identically; they differ only in which suggestion
    // list they fill and what applyPlace writes back into them.
    const bindSearch = (selector, kind) => {
      const input = this.querySelector(selector);
      if (!input) return;
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.suggest(input.value, kind), 600);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.suggest(input.value, kind);
        }
      });
    };
    bindSearch('[data-city]', 'city');
    bindSearch('[data-address]', 'address');

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.hideSuggest('city');
        this.hideSuggest('address');
      }
    });

    // Three optional overrides. Blank means "use the generated value", so the
    // customer only types where they want to differ from the map.
    const bindOverride = (selector, key) => {
      const el = this.querySelector(selector);
      if (!el) return;
      el.addEventListener('input', () => {
        this.overrides[key] = el.value.trim();
        this.updateCaption();
        this.updateForm();
      });
    };
    bindOverride('[data-caption-place]', 'place');
    bindOverride('[data-caption-country]', 'country');
    bindOverride('[data-caption-coords]', 'coords');

    this.querySelectorAll('[data-orientation-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.orientation = btn.dataset.orientationValue;
        this.querySelectorAll('[data-orientation-value]').forEach((b) =>
          b.classList.toggle('is-active', b === btn)
        );
        if (this.frame) this.frame.dataset.orientation = this.orientation;
        this.updateForm();
        if (this.map) setTimeout(() => this.map.resize(), 60);
      });
    });
  }

  money(cents) {
    const value = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'INR',
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch (err) {
      return `${value}`;
    }
  }

  setStatus(text, isError = false) {
    if (!this.status) return;
    this.status.hidden = false;
    this.status.classList.toggle('is-error', isError);
    if (this.statusText) this.statusText.textContent = text;
  }

  hideStatus() {
    if (this.status) this.status.hidden = true;
  }

  updateForm() {
    if (!this.formFields) return;
    const variant = this.variants[this.formatIndex];
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput && variant) idInput.value = variant.id;

    const price = this.querySelector('[data-price]');
    if (price && variant) price.textContent = this.money(variant.price);

    this.syncFormFields();
  }

  /** Write the current recipe into the hidden inputs. */
  syncFormFields() {
    const variant = this.variants[this.formatIndex];

    const o = (k) => (this.overrides || {})[k] || '';
    const set = (name, value) => {
      if (this.formFields[name]) this.formFields[name].value = value ?? '';
    };
    set('Latitude', this.view.lat.toFixed(6));
    set('Longitude', this.view.lon.toFixed(6));
    set('Pin', this.pin ? `${this.pin.lat.toFixed(6)}, ${this.pin.lon.toFixed(6)}` : '');
    set('Span (m)', Math.round(this.view.spanM));
    set('Zoom', this.map ? this.map.getZoom().toFixed(3) : '');
    set('Palette', this.palette?.label || '');
    set('Place', this.place || '');
    set('Country', this.country || '');
    set('Caption', [o('place'), o('country'), o('coords')].filter(Boolean).join(' | '));
    set('Orientation', this.orientation);
    set('Label style', this.labelStyle);
    set('Font', (FONTS.find((f) => f.id === this.font) || FONTS[0]).label);
    set('Format', variant?.title || '');
  }

  /** Write the recipe, upload the poster preview, then submit for real. */
  async onSubmit(event) {
    this.syncFormFields();
    if (!this.formFields || !this.formFields['_Poster preview']) return;
    if (this.skipNext) {
      // second pass, triggered below: let the theme's own handler run
      this.skipNext = false;
      return;
    }
    const form = event.target;
    event.preventDefault();
    // preventDefault alone does not stop the theme's own submit listener: it
    // would still add to the cart straight away, without the poster, and then
    // again once the upload finishes. Keep this first pass to ourselves.
    event.stopImmediatePropagation();
    this.setStatus('Preparing your poster\u2026');
    let failed = false;
    try {
      // never let a slow capture or a stalled upload hold the checkout hostage
      await Promise.race([
        this.uploadPoster(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('upload timed out')), 30000)),
      ]);
    } catch (err) {
      failed = true;
      console.warn('map poster preview upload failed', err);
      this.setStatus('Sorry \u2014 we could not prepare your poster. You can still order; we\u2019ll follow up.', true);
    }
    if (!failed) this.hideStatus();
    // re-submit through the theme so its add-to-cart (drawer/notification) runs
    this.skipNext = true;
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  }

  /**
   * Render the current map to a PNG and store it via the uploads Worker.
   *
   * Captured at print-ish resolution: the map is temporarily moved off-screen
   * and resized so its drawing buffer reaches the target long edge (capped by
   * the GPU), the viewport is redrawn and its tiles settle, then it is captured
   * and the on-screen map restored. This lifts the poster from the ~700px
   * preview to a few thousand px, in the customer's own browser, for free.
   */
  async uploadPoster() {
    const base = this.uploadBase();
    if (!base || !this.map) return;
    const blob = await this.capturePoster();
    if (!blob) return;
    // The cart shows a small copy of the poster, not the poster itself: the
    // preview is several thousand pixels wide and would cost the drawer a
    // multi-megabyte image. A thumbnail failure must not block the order, so it
    // is uploaded alongside and simply left out when it fails.
    const thumb = await this.makeThumbnail(blob).catch(() => null);
    const [posterUrl, thumbUrl] = await Promise.all([
      this.sendFile(base, blob, 'poster.png'),
      thumb ? this.sendFile(base, thumb, 'poster-thumb.jpg').catch(() => '') : '',
    ]);
    if (posterUrl) this.formFields['_Poster preview'].value = posterUrl;
    if (thumbUrl) this.formFields['_Poster thumbnail'].value = thumbUrl;
  }

  /** POST one rendered file to the uploads Worker and return its public URL. */
  async sendFile(base, blob, name) {
    const body = new FormData();
    body.append('file', blob, name);
    body.append('product', 'custom-map-poster');
    body.append('kind', 'map');
    const res = await fetch(`${base}/upload`, { method: 'POST', body });
    if (!res.ok) throw new Error(`upload ${res.status}`);
    const data = await res.json();
    return (data && data.url) || '';
  }

  /**
   * A square, cart-sized copy of the poster. The drawer's media box is 150px,
   * so 480px covers retina at a few tens of KB. The poster is letterboxed on
   * white, the colour of the paper it prints on, so the square never crops the
   * map or the caption.
   */
  async makeThumbnail(blob) {
    const src = await this.decodeBlob(blob);
    if (!src) return null;
    const size = 480;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    const k = Math.min(size / src.width, size / src.height);
    const w = Math.max(1, Math.round(src.width * k));
    const h = Math.max(1, Math.round(src.height * k));
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, Math.round((size - w) / 2), Math.round((size - h) / 2), w, h);
    if (src.close) src.close();
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
  }

  /** Decode a PNG blob into something drawable, preferring ImageBitmap. */
  async decodeBlob(blob) {
    if (window.createImageBitmap) {
      try {
        return await createImageBitmap(blob);
      } catch { /* fall through to an <img> decode */ }
    }
    return await new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  /** The GPU's max texture size, which caps the drawing buffer. */
  maxTextureSize() {
    try {
      const gl = this.map.getCanvas().getContext('webgl2') ||
        this.map.getCanvas().getContext('webgl');
      return (gl && gl.getParameter(gl.MAX_TEXTURE_SIZE)) || 4096;
    } catch {
      return 4096;
    }
  }

  /**
   * Capture the poster at the highest sensible resolution, then restore it.
   *
   * The map is grabbed at high resolution (below), then composited into a
   * poster: white sheet, the map inset with its hairline border, and the caption
   * drawn on top. The layout is read from the live DOM (fractions of the
   * poster box), so the composite matches the CSS rather than guessing at it.
   */
  async capturePoster() {
    const layout = this.readPosterLayout();
    if (!layout) return null;
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* fonts never block the capture */ }
    }

    const target = Number(this.dataset.posterPx) || 5000;
    const cap = Math.min(this.maxTextureSize(), 8192) - 64;

    const aspect = layout.poster.h / layout.poster.w; // >1 portrait, <1 landscape
    let posterW, posterH;
    if (aspect >= 1) { posterH = target; posterW = Math.round(target / aspect); }
    else { posterW = target; posterH = Math.round(target * aspect); }

    // Some browsers cap the canvas AREA (~16.7M px on Safari), past which
    // toBlob() returns null and the poster silently fails to upload. Stay under.
    const MAX_AREA = 16_000_000;
    const area = posterW * posterH;
    if (area > MAX_AREA) {
      const k = Math.sqrt(MAX_AREA / area);
      posterW = Math.max(1, Math.round(posterW * k));
      posterH = Math.max(1, Math.round(posterH * k));
    }

    let mapW = Math.round(layout.map.w * posterW);
    let mapH = Math.round(layout.map.h * posterH);
    const shrink = Math.min(1, cap / Math.max(mapW, mapH));
    mapW = Math.max(1, Math.round(mapW * shrink));
    mapH = Math.max(1, Math.round(mapH * shrink));

    const mapCanvas = await this.grabMap(mapW, mapH);
    if (!mapCanvas) return null;
    return await this.composePoster(layout, posterW, posterH, mapCanvas);
  }

  /** Read the poster's layout as fractions of its own box. */
  readPosterLayout() {
    const poster = this.querySelector('.map-editor__poster');
    const mapwrap = this.querySelector('.map-editor__mapwrap');
    if (!poster || !mapwrap) return null;
    const pr = poster.getBoundingClientRect();
    const rel = (r) => ({
      x: (r.left - pr.left) / pr.width,
      y: (r.top - pr.top) / pr.height,
      w: r.width / pr.width,
      h: r.height / pr.height,
    });
    const lines = ['[data-cap-title]', '[data-cap-sub]', '[data-cap-coords]']
      .map((sel) => {
        const el = this.querySelector(sel);
        if (!el || !el.textContent.trim()) return null;
        const cs = getComputedStyle(el);
        return {
          text: el.textContent,
          box: rel(el.getBoundingClientRect()),
          fontSize: parseFloat(cs.fontSize) || 0,
          weight: cs.fontWeight || '400',
          family: cs.fontFamily || 'sans-serif',
          spacing: parseFloat(cs.letterSpacing) || 0,
          upper: cs.textTransform === 'uppercase',
          opacity: parseFloat(cs.opacity),
          align: cs.textAlign || 'left',
        };
      })
      .filter(Boolean);
    return { poster: { w: pr.width, h: pr.height }, map: rel(mapwrap.getBoundingClientRect()), lines };
  }

  /** Enlarge the map off-page to `bufW`x`bufH`, snapshot it, then restore it. */
  async grabMap(bufW, bufH) {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    const oldW = container.clientWidth || 1;
    const baseZoom = this.map.getZoom();
    const prev = {
      width: container.style.width, height: container.style.height,
      position: container.style.position, left: container.style.left,
      top: container.style.top,
    };
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '-100000px';
    container.style.width = `${Math.round(bufW / dpr)}px`;
    container.style.height = `${Math.round(bufH / dpr)}px`;
    this.map.resize();
    // A bigger viewport shows MORE ground at the same zoom. Raise the zoom by
    // log2(scale) so the capture frames exactly what the customer saw.
    const scale = (bufW / dpr) / oldW;
    this.map.setZoom(baseZoom + Math.log2(scale));

    let snapshot = null;
    try {
      this.map.triggerRepaint();
      await new Promise((resolve) => {
        const t0 = Date.now();
        const tick = () => {
          if (this.map.areTilesLoaded() && this.map.loaded()) return resolve();
          if (Date.now() - t0 > 15000) return resolve();
          setTimeout(tick, 150);
        };
        tick();
      });
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      // copy the pixels NOW: resizing the map back clears the WebGL buffer
      snapshot = await this.snapshot(this.map.getCanvas());
    } finally {
      container.style.width = prev.width;
      container.style.height = prev.height;
      container.style.position = prev.position;
      container.style.left = prev.left;
      container.style.top = prev.top;
      this.map.resize();
      this.map.setZoom(baseZoom);
    }
    return snapshot;
  }

  /** Copy a (WebGL) canvas to something drawable elsewhere. */
  async snapshot(canvas) {
    if (window.createImageBitmap) {
      try {
        return await createImageBitmap(canvas);
      } catch { /* fall through to a 2D copy */ }
    }
    const copy = document.createElement('canvas');
    copy.width = canvas.width;
    copy.height = canvas.height;
    copy.getContext('2d').drawImage(canvas, 0, 0);
    return copy;
  }

  /** Draw the poster: sheet, map inset, caption. */
  async composePoster(layout, posterW, posterH, mapCanvas) {
    // Re-draw at progressively smaller sizes until the canvas encodes. Some
    // browsers return a null blob past a canvas area/size limit (Safari caps
    // area ~16.7M px); rather than lose the poster, step down and try again.
    for (;;) {
      const blob = await this.drawPoster(layout, posterW, posterH, mapCanvas);
      if (blob) return blob;
      if (posterW <= 1000) throw new Error(`poster could not be encoded (${posterW}x${posterH})`);
      posterW = Math.round(posterW * 0.72);
      posterH = Math.round(posterH * 0.72);
    }
  }

  async drawPoster(layout, posterW, posterH, mapCanvas) {
    const canvas = document.createElement('canvas');
    canvas.width = posterW;
    canvas.height = posterH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, posterW, posterH);

    const scale = posterW / layout.poster.w;
    const mx = layout.map.x * posterW;
    const my = layout.map.y * posterH;
    const mw = layout.map.w * posterW;
    const mh = layout.map.h * posterH;
    const border = Math.max(1, Math.round(1 * scale));

    ctx.drawImage(mapCanvas, mx + border, my + border, mw - 2 * border, mh - 2 * border);
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = border;
    ctx.strokeRect(mx + border / 2, my + border / 2, mw - border, mh - border);

    ctx.fillStyle = '#111111';
    ctx.textBaseline = 'top';
    for (const line of layout.lines) {
      const px = line.fontSize * scale;
      ctx.font = `${line.weight} ${px}px ${line.family}`;
      if ('letterSpacing' in ctx) ctx.letterSpacing = `${line.spacing * scale}px`;
      ctx.globalAlpha = Number.isNaN(line.opacity) ? 1 : line.opacity;
      const text = line.upper ? line.text.toUpperCase() : line.text;
      const x = line.box.x * posterW;
      const y = line.box.y * posterH;
      const w = line.box.w * posterW;
      if (line.align === 'center') { ctx.textAlign = 'center'; ctx.fillText(text, x + w / 2, y); }
      else if (line.align === 'right') { ctx.textAlign = 'right'; ctx.fillText(text, x + w, y); }
      else { ctx.textAlign = 'left'; ctx.fillText(text, x, y); }
    }
    ctx.globalAlpha = 1;
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }

  uploadBase() {
    const explicit = (this.dataset.uploadUrl || '').trim();
    if (explicit) return explicit.replace(/\/$/, '');
    try {
      return new URL(this.dataset.tileUrl).origin;
    } catch {
      return '';
    }
  }
}

function dms(value, isLat) {
  const hemi = isLat ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
  const v = Math.abs(value);
  const d = Math.floor(v);
  const m = Math.floor((v - d) * 60);
  const s = Math.round((v - d - m / 60) * 3600);
  return `${d}\u00b0${String(m).padStart(2, '0')}'${String(s).padStart(2, '0')}" ${hemi}`;
}

if (!customElements.get('map-poster-editor')) {
  customElements.define('map-poster-editor', MapPosterEditor);
}
